import PaginatedResponse, { Paginated } from '_shared/_api/pagination/responses/paginated-response';
import { api } from '_shared/_stores/api';
import { logSchemaFailure } from '_shared/_utilities/log-schema-failure';
import Guid from '_shared/guids/guid';
import CreateNetworkedGroupApiRequest from 'users/networking/groups/_api/commands/create-networked-group-api-command';
import UpdateNetworkedGroupApiRequest from 'users/networking/groups/_api/commands/update-networked-group-api-command';
import NetworkedGroupQuery from 'users/networking/groups/_api/responses/networked-groups-api-query';
import NetworkedGroupSchema, { NetworkedGroupJSON } from 'users/networking/groups/_schemas/networked-group-schema';
import z from 'zod';

export const networkedGroupApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getNetworkedGroupById: (
      build.query<NetworkedGroupJSON, Guid>({
        query: (id: Guid) => ({
          url: `/users/network/groups/${id.value}`,
        }),
        rawResponseSchema: NetworkedGroupSchema.NetworkedGroupJSONZ,
        onSchemaFailure: logSchemaFailure,
        providesTags: (result, error, id) => [
          { type: "NetworkedGroup" as const, id: id.value }
        ]
      })
    ),
    queryNetworkedGroups: (
      build.query<Paginated<NetworkedGroupJSON>, NetworkedGroupQuery>({
        query: (query: NetworkedGroupQuery) => ({
          url: '/users/network/groups',
          params: query.searchParams
        }),
        rawResponseSchema: z.array(NetworkedGroupSchema.NetworkedGroupJSONZ),
        onSchemaFailure: logSchemaFailure,
        transformResponse: (response: NetworkedGroupJSON[], meta): Paginated<NetworkedGroupJSON> => {
          const headers = meta?.response?.headers.get('x-pagination') ?? '';
          return {
            data: response,
            headers: PaginatedResponse.parsePaginationHeader(headers)
          };
        },
        providesTags: (result) =>
          result
            ? [
              // Tag each item
              ...result?.data.map(group => ({
                type: "NetworkedGroup" as const,
                id: group.id,
              })) ?? [],
              // Tag the list itself
              { type: "NetworkedGroup" as const, id: "LIST" },
            ]
            : [{ type: "NetworkedGroup" as const, id: "LIST" }],
      })
    ),
    // #endregion

    // #region Create
    createNetworkedGroup: (
      build.mutation<NetworkedGroupJSON, CreateNetworkedGroupApiRequest>({
        query: (query: CreateNetworkedGroupApiRequest) => ({
          url: `users/network/groups`,
          method: 'POST',
          body: query
        }),
        rawResponseSchema: NetworkedGroupSchema.NetworkedGroupJSONZ,
        onSchemaFailure: logSchemaFailure,
        invalidatesTags: [{
          type: 'NetworkedGroup',
          id: 'LIST'
        }]
      })
    ),
    // #endregion

    // #region Update
    updateNetworkedGroup: (
      build.mutation<NetworkedGroupJSON, { id: Guid; data: UpdateNetworkedGroupApiRequest }>({
        query: ({ id, data }) => ({
          url: `users/network/groups/${id.value}`,
          method: 'PATCH',
          body: data.payload,
          headers: data.expectedVersion ? { 'If-Match': data.expectedVersion } : undefined
        }),
        rawResponseSchema: NetworkedGroupSchema.NetworkedGroupJSONZ,
        onSchemaFailure: logSchemaFailure,
        invalidatesTags: (result, _error, { id }) => result
          ? [
            { type: 'NetworkedGroup', id: id.value },
            { type: 'NetworkedGroup', id: 'LIST' }
          ]
          // A response/schema failure can happen after the server has committed the update.
          // Refresh the collection, but never retry the mutation itself.
          : [{ type: 'NetworkedGroup', id: 'LIST' }]
      })
    ),
    // #endregion
  })
});

export const {
  useGetNetworkedGroupByIdQuery,
  useQueryNetworkedGroupsQuery,
  useCreateNetworkedGroupMutation,
  useUpdateNetworkedGroupMutation
} = networkedGroupApi;
